// ShopMode — top-level Shop Mode container. Owns the three-phase flow
// (scanning → resolving → checkout), mounts the trip-scoped realtime
// channel, and routes between phase screens. Replaces the legacy
// ShopMode.jsx + ShopModeCheckout.jsx + ShopModePlacementWizard.jsx
// trio (~5,000 lines combined) with a phase-routed shell on top of the
// shared trip store.

import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { getLastStore } from "../../lib/useActiveStore";
import { getShoppingRegion } from "../../lib/useShoppingRegion";
import { storeCatalogForRegion } from "../../data/storeCatalog";
import { normalizeStoreName } from "../../data/brandExpertise";
import {
  useShopTrip,
  useTripRealtime,
  tripActions,
} from "../../lib/useShopTrip";
import ScanCapture from "./ScanCapture";
import TripList from "./TripList";
import ResolveScreen from "./ResolveScreen";
import CheckoutScreen from "./CheckoutScreen";
import { COLOR, FONT, RADIUS, SPACE, Z } from "../../lib/tokens";

const overlayShellStyle = {
  position: "fixed",
  inset: 0,
  background: COLOR.night,
  zIndex: Z.sheet,
  display: "flex",
  flexDirection: "column",
  // Do NOT set height: 100vh. On iOS Safari 100vh is the URL-bar-
  // hidden viewport, so the shell extends past the visible screen
  // when the URL bar is showing and the footer drops below the fold.
  // `inset: 0` already pins the element to the visible viewport
  // and resizes correctly when the URL bar shows/hides.
};

const headerStyle = {
  display: "flex",
  alignItems: "center",
  gap: SPACE.md,
  // Top padding accommodates the iOS notch + a comfortable 14px above
  // the title. Horizontal padding gives breathing room from the edge.
  padding: `calc(env(safe-area-inset-top, 0px) + 14px) 18px 14px 18px`,
  borderBottom: `1px solid ${COLOR.edge}`,
  flex: "0 0 auto",
};

const titleStyle = {
  flex: 1,
  minWidth: 0,
  fontFamily: FONT.itemTitle,
  fontStyle: "italic",
  fontSize: 22,
  color: COLOR.ink,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const phaseLabelStyle = {
  fontFamily: FONT.mono,
  fontSize: 9,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: COLOR.muted,
  whiteSpace: "nowrap",
};

const closeBtnStyle = {
  fontFamily: FONT.mono,
  fontSize: 11,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: COLOR.muted,
  background: "transparent",
  border: "none",
  padding: "6px 10px",
  cursor: "pointer",
  flex: "0 0 auto",
};

const ctaBarStyle = {
  display: "flex",
  gap: SPACE.md,
  // Bottom inset accommodates the iOS home-indicator + 14px above it.
  padding: `14px 18px calc(env(safe-area-inset-bottom, 0px) + 14px) 18px`,
  borderTop: `1px solid ${COLOR.edge}`,
  background: COLOR.night,
  flex: "0 0 auto",
};

const ctaPrimaryStyle = {
  flex: 1,
  padding: "12px 16px",
  fontFamily: FONT.mono,
  fontSize: 12,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: COLOR.night,
  background: COLOR.leaf,
  border: "none",
  borderRadius: RADIUS.md,
  cursor: "pointer",
};

const ctaSecondaryStyle = {
  padding: "12px 16px",
  fontFamily: FONT.mono,
  fontSize: 12,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: COLOR.muted,
  background: "transparent",
  border: `1px solid ${COLOR.edge}`,
  borderRadius: RADIUS.md,
  cursor: "pointer",
};

export default function ShopMode({
  userId,
  isAdmin = false,
  shoppingList = [],
  onRemoveShoppingItem,
  onClose,
}) {
  const activeTrip = useShopTrip((s) => s.activeTrip);
  const phase = useShopTrip((s) => s.phase);
  const loading = useShopTrip((s) => s.loading);
  const scansCount = useShopTrip((s) => s.scanOrder.length);

  // Mount: hydrate any active trip (resume), open the realtime channel.
  useEffect(() => {
    tripActions.refreshActive(userId, isAdmin);
  }, [userId, isAdmin]);

  useTripRealtime(activeTrip?.id);

  // "Where are you shopping?" — one-tap store pick at trip start.
  // Powers house-brand boosting (at Costco → Kirkland first) for the
  // whole trip. Never a gate: Start Trip works with nothing picked.
  // Pills = the user's own recent stores (recency-first) + curated
  // catalog stores relevant to their region; free text for the rest,
  // stored VERBATIM (matching normalizes at compare time only).
  const [storePick, setStorePick] = useState(() => getLastStore());
  const [storeCustom, setStoreCustom] = useState("");
  const [recentStores, setRecentStores] = useState([]);
  useEffect(() => {
    if (activeTrip || !userId) return;
    let alive = true;
    (async () => {
      const { data } = await supabase
        .from("shopping_trips")
        .select("store_name")
        .not("store_name", "is", null)
        .order("started_at", { ascending: false })
        .limit(20);
      if (!alive || !Array.isArray(data)) return;
      const seen = new Set();
      const out = [];
      for (const row of data) {
        const name = String(row.store_name || "").trim();
        const key = normalizeStoreName(name);
        if (!name || !key || seen.has(key)) continue;
        seen.add(key);
        out.push(name);
      }
      setRecentStores(out);
    })();
    return () => { alive = false; };
  }, [activeTrip, userId]);

  const storePills = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const name of recentStores) {
      const key = normalizeStoreName(name);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(name);
    }
    for (const entry of storeCatalogForRegion(getShoppingRegion(), { limit: 8 })) {
      const key = normalizeStoreName(entry.name);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(entry.name);
    }
    return out.slice(0, 6);
  }, [recentStores]);

  async function handleStart() {
    const storeName = storeCustom.trim() || storePick || null;
    await tripActions.start({ storeName });
  }

  async function handleCancel() {
    await tripActions.cancel();
    onClose?.();
  }

  function handleClose() {
    onClose?.();
  }

  // Pre-trip welcome (no active trip yet)
  if (loading) {
    return (
      <div style={overlayShellStyle}>
        <div style={headerStyle}>
          <div style={titleStyle}>Shop Mode</div>
          <button onClick={handleClose} style={closeBtnStyle}>Close</button>
        </div>
        <div style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: FONT.forma,
          fontStyle: "italic",
          fontSize: 14,
          color: COLOR.muted,
        }}>
          Loading…
        </div>
      </div>
    );
  }

  if (!activeTrip) {
    return (
      <div style={overlayShellStyle}>
        <div style={headerStyle}>
          <div style={titleStyle}>Shop Mode</div>
          <button onClick={handleClose} style={closeBtnStyle}>Close</button>
        </div>
        <div style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: SPACE.lg,
          padding: SPACE.section,
        }}>
          <div style={{
            fontFamily: FONT.itemTitle,
            fontStyle: "italic",
            fontSize: 28,
            color: COLOR.ink,
            textAlign: "center",
          }}>
            Ready when you are.
          </div>
          <div style={{
            fontFamily: FONT.forma,
            fontSize: 14,
            color: COLOR.muted,
            textAlign: "center",
            maxWidth: 320,
          }}>
            Scan items as you put them in the cart. Pair to your shopping list. Resolve unknowns later. Bulk-add everything when you check out.
          </div>
          <div style={{ width: "100%", maxWidth: 340, display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{
              fontFamily: FONT.mono,
              fontSize: 12,
              letterSpacing: "0.14em",
              color: COLOR.dim,
              textAlign: "center",
            }}>
              WHERE ARE YOU SHOPPING?
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center" }}>
              {storePills.map((name) => {
                const selected = !storeCustom.trim() && storePick === name;
                return (
                  <button
                    key={name}
                    className="mcm-focusable"
                    onClick={() => { setStorePick(selected ? null : name); setStoreCustom(""); }}
                    style={{
                      padding: "8px 14px",
                      borderRadius: 999,
                      border: `1.5px solid ${selected ? COLOR.leaf : COLOR.border}`,
                      background: selected ? "rgba(47,143,131,0.12)" : "transparent",
                      color: selected ? COLOR.leaf : COLOR.ink,
                      fontFamily: FONT.forma,
                      fontSize: 13,
                      fontWeight: selected ? 600 : 500,
                      cursor: "pointer",
                    }}
                  >
                    {name}
                  </button>
                );
              })}
            </div>
            <input
              type="text"
              value={storeCustom}
              onChange={(e) => setStoreCustom(e.target.value)}
              placeholder="Somewhere else — type it (optional)"
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "10px 14px",
                borderRadius: 12,
                border: `1px solid ${storeCustom.trim() ? COLOR.leaf : COLOR.border}`,
                background: "transparent",
                color: COLOR.ink,
                fontFamily: FONT.forma,
                fontSize: 14,
                outline: "none",
                textAlign: "center",
              }}
            />
          </div>
          <button onClick={handleStart} style={{ ...ctaPrimaryStyle, flex: "0 0 auto", minWidth: 240 }}>
            {(storeCustom.trim() || storePick)
              ? `Start trip at ${storeCustom.trim() || storePick}`
              : "Start trip"}
          </button>
        </div>
      </div>
    );
  }

  // Active trip — phase router. Header carries the title + a single
  // phase label + close button. No tab strip — the body always makes
  // the current phase obvious, and three chips don't survive a phone
  // viewport.
  const phaseLabel = phase === "scanning"
    ? "Scanning"
    : phase === "resolving"
      ? "Resolving"
      : "Checkout";
  return (
    <div style={overlayShellStyle}>
      <div style={headerStyle}>
        <div style={titleStyle}>Shop Mode</div>
        <span style={phaseLabelStyle}>{phaseLabel}</span>
        <button onClick={handleClose} style={closeBtnStyle}>Close</button>
      </div>

      {phase === "scanning" && (
        <>
          {/* Scanner takes ~50vh at the top so it dominates the screen
              on phone but leaves room for the LastScanPanel +
              cart/list below. */}
          <div style={{
            flex: "0 0 50vh",
            minHeight: 0,
            position: "relative",
            overflow: "hidden",
            background: "#000",
          }}>
            <ScanCapture onCancel={handleClose} shoppingList={shoppingList} />
          </div>
          {/* Trip list claims the remaining space and scrolls. Cart
              renders above the shopping list (newest scan auto-
              expanded at top so every axis the resolver bound is
              visible AND tappable for correction without leaving
              the scanner view). */}
          <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            <TripList
              shoppingList={shoppingList}
              onRemoveShoppingItem={onRemoveShoppingItem}
            />
          </div>
          <div style={ctaBarStyle}>
            <button onClick={handleCancel} style={ctaSecondaryStyle}>
              Cancel trip
            </button>
            <button
              onClick={() => tripActions.setPhase("resolving")}
              style={ctaPrimaryStyle}
              disabled={scansCount === 0}
            >
              Done shopping ({scansCount})
            </button>
          </div>
        </>
      )}

      {phase === "resolving" && (
        <ResolveScreen
          shoppingList={shoppingList}
          onBack={() => tripActions.setPhase("scanning")}
          onContinue={() => tripActions.setPhase("checkout")}
        />
      )}

      {phase === "checkout" && (
        <CheckoutScreen
          userId={userId}
          isAdmin={isAdmin}
          onBack={() => tripActions.setPhase("resolving")}
          onComplete={() => onClose?.()}
        />
      )}
    </div>
  );
}
